import React, { useState, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, TruckIcon, CreditCardIcon, UserIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { useCart } from '../context/CartContext';
type Step = 1 | 2 | 3;
type DeliveryMethod = 'nova-poshta' | 'courier';
type PaymentMethod = 'card' | 'cod';
export function CheckoutPage() {
  const navigate = useNavigate();
  const {
    cart,
    totalPrice,
    clearCart
  } = useCart();
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    deliveryMethod: 'nova-poshta' as DeliveryMethod,
    city: '',
    warehouse: '',
    address: '',
    paymentMethod: 'card' as PaymentMethod,
    comment: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uk-UA').format(price);
  };
  const deliveryPrice = totalPrice >= 5000 ? 0 : 150;
  const finalTotal = totalPrice + deliveryPrice;
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };
  const validateStep = (step: Step): boolean => {
    const newErrors: Record<string, string> = {};
    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = "Введіть ім'я";
      if (!formData.lastName.trim()) newErrors.lastName = 'Введіть прізвище';
      if (!formData.phone.trim()) newErrors.phone = 'Введіть телефон';
      if (!formData.email.trim()) newErrors.email = 'Введіть email';
    }
    if (step === 2) {
      if (!formData.city.trim()) newErrors.city = 'Введіть місто';
      if (formData.deliveryMethod === 'nova-poshta' && !formData.warehouse.trim()) {
        newErrors.warehouse = 'Оберіть відділення';
      }
      if (formData.deliveryMethod === 'courier' && !formData.address.trim()) {
        newErrors.address = 'Введіть адресу';
      }
    }
    if (step === 3) {
      if (!formData.agreeToTerms) newErrors.agreeToTerms = 'Погодьтесь з умовами';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3) as Step);
    }
  };
  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1) as Step);
  };
  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    clearCart();
    navigate('/thank-you');
  };
  const steps = [{
    number: 1,
    title: 'Контакти',
    icon: UserIcon
  }, {
    number: 2,
    title: 'Доставка',
    icon: TruckIcon
  }, {
    number: 3,
    title: 'Оплата',
    icon: CreditCardIcon
  }];
  if (cart.length === 0) {
    return <main className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-576">
        <div className="text-center" data-id="element-577">
          <h1 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-578">
            Кошик порожній
          </h1>
          <Link to="/catalog" className="text-sky-600 hover:text-sky-700" data-id="element-579">
            Перейти до каталогу
          </Link>
        </div>
      </main>;
  }
  return <main className="min-h-screen bg-slate-50" data-id="element-580">
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-slate-100" data-id="element-581">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-582">
          <nav className="flex items-center gap-2 text-sm" data-id="element-583">
            <Link to="/" className="text-slate-500 hover:text-slate-700" data-id="element-584">
              Головна
            </Link>
            <span className="text-slate-300" data-id="element-585">/</span>
            <Link to="/cart" className="text-slate-500 hover:text-slate-700" data-id="element-586">
              Кошик
            </Link>
            <span className="text-slate-300" data-id="element-587">/</span>
            <span className="text-slate-900 font-medium" data-id="element-588">
              Оформлення замовлення
            </span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-589">
        <h1 className="text-3xl font-bold text-slate-900 mb-8" data-id="element-590">
          Оформлення замовлення
        </h1>

        {/* Progress Steps */}
        <div className="mb-8" data-id="element-591">
          <div className="flex items-center justify-center" data-id="element-592">
            {steps.map((step, index) => <Fragment key={step.number} data-id="element-593">
                <div className={`flex items-center gap-3 ${currentStep >= step.number ? 'text-sky-600' : 'text-slate-400'}`} data-id="element-594">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentStep > step.number ? 'bg-sky-500 text-white' : currentStep === step.number ? 'bg-sky-100 text-sky-600 ring-2 ring-sky-500' : 'bg-slate-100 text-slate-400'}`} data-id="element-595">
                    {currentStep > step.number ? <CheckIcon className="w-5 h-5" data-id="element-596" /> : step.number}
                  </div>
                  <span className="font-medium hidden sm:block" data-id="element-597">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && <div className={`w-16 sm:w-24 h-0.5 mx-4 ${currentStep > step.number ? 'bg-sky-500' : 'bg-slate-200'}`} data-id="element-598" />}
              </Fragment>)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-599">
          {/* Form */}
          <div className="lg:col-span-2" data-id="element-600">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="bg-white rounded-2xl shadow-md p-6 lg:p-8" data-id="element-601">
              {/* Step 1: Contact Info */}
              {currentStep === 1 && <div className="space-y-6" data-id="element-602">
                  <h2 className="text-xl font-semibold text-slate-900 mb-6" data-id="element-603">
                    Контактні дані
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-604">
                    <Input label="Ім'я" value={formData.firstName} onChange={e => handleInputChange('firstName', e.target.value)} error={errors.firstName} placeholder="Введіть ім'я" data-id="element-605" />
                    <Input label="Прізвище" value={formData.lastName} onChange={e => handleInputChange('lastName', e.target.value)} error={errors.lastName} placeholder="Введіть прізвище" data-id="element-606" />
                  </div>
                  <Input label="Телефон" type="tel" value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} error={errors.phone} placeholder="+38 (0__) ___-__-__" data-id="element-607" />
                  <Input label="Email" type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} error={errors.email} placeholder="email@example.com" data-id="element-608" />
                </div>}

              {/* Step 2: Delivery */}
              {currentStep === 2 && <div className="space-y-6" data-id="element-609">
                  <h2 className="text-xl font-semibold text-slate-900 mb-6" data-id="element-610">
                    Спосіб доставки
                  </h2>

                  <div className="space-y-3" data-id="element-611">
                    <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.deliveryMethod === 'nova-poshta' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-612">
                      <input type="radio" name="delivery" checked={formData.deliveryMethod === 'nova-poshta'} onChange={() => handleInputChange('deliveryMethod', 'nova-poshta')} className="w-5 h-5 text-sky-500" data-id="element-613" />
                      <div className="flex-1" data-id="element-614">
                        <p className="font-medium text-slate-900" data-id="element-615">Нова Пошта</p>
                        <p className="text-sm text-slate-500" data-id="element-616">
                          Доставка у відділення
                        </p>
                      </div>
                      <span className="text-sm font-medium text-slate-900" data-id="element-617">
                        {totalPrice >= 5000 ? 'Безкоштовно' : '150 ₴'}
                      </span>
                    </label>

                    <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.deliveryMethod === 'courier' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-618">
                      <input type="radio" name="delivery" checked={formData.deliveryMethod === 'courier'} onChange={() => handleInputChange('deliveryMethod', 'courier')} className="w-5 h-5 text-sky-500" data-id="element-619" />
                      <div className="flex-1" data-id="element-620">
                        <p className="font-medium text-slate-900" data-id="element-621">Кур'єр</p>
                        <p className="text-sm text-slate-500" data-id="element-622">
                          Доставка за адресою
                        </p>
                      </div>
                      <span className="text-sm font-medium text-slate-900" data-id="element-623">
                        від 200 ₴
                      </span>
                    </label>
                  </div>

                  <Input label="Місто" value={formData.city} onChange={e => handleInputChange('city', e.target.value)} error={errors.city} placeholder="Введіть місто" data-id="element-624" />

                  {formData.deliveryMethod === 'nova-poshta' && <Select label="Відділення Нової Пошти" value={formData.warehouse} onChange={e => handleInputChange('warehouse', e.target.value)} error={errors.warehouse} placeholder="Оберіть відділення" options={[{
                value: '1',
                label: 'Відділення №1'
              }, {
                value: '2',
                label: 'Відділення №2'
              }, {
                value: '3',
                label: 'Відділення №3'
              }, {
                value: '4',
                label: 'Поштомат №4'
              }, {
                value: '5',
                label: 'Поштомат №5'
              }]} data-id="element-625" />}

                  {formData.deliveryMethod === 'courier' && <Input label="Адреса доставки" value={formData.address} onChange={e => handleInputChange('address', e.target.value)} error={errors.address} placeholder="Вулиця, будинок, квартира" data-id="element-626" />}
                </div>}

              {/* Step 3: Payment */}
              {currentStep === 3 && <div className="space-y-6" data-id="element-627">
                  <h2 className="text-xl font-semibold text-slate-900 mb-6" data-id="element-628">
                    Спосіб оплати
                  </h2>

                  <div className="space-y-3" data-id="element-629">
                    <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.paymentMethod === 'card' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-630">
                      <input type="radio" name="payment" checked={formData.paymentMethod === 'card'} onChange={() => handleInputChange('paymentMethod', 'card')} className="w-5 h-5 text-sky-500" data-id="element-631" />
                      <div className="flex-1" data-id="element-632">
                        <p className="font-medium text-slate-900" data-id="element-633">
                          Оплата карткою онлайн
                        </p>
                        <p className="text-sm text-slate-500" data-id="element-634">
                          Visa, Mastercard
                        </p>
                      </div>
                    </label>

                    <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.paymentMethod === 'cod' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-635">
                      <input type="radio" name="payment" checked={formData.paymentMethod === 'cod'} onChange={() => handleInputChange('paymentMethod', 'cod')} className="w-5 h-5 text-sky-500" data-id="element-636" />
                      <div className="flex-1" data-id="element-637">
                        <p className="font-medium text-slate-900" data-id="element-638">Післяплата</p>
                        <p className="text-sm text-slate-500" data-id="element-639">
                          Оплата при отриманні
                        </p>
                      </div>
                    </label>
                  </div>

                  <div data-id="element-640">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-641">
                      Коментар до замовлення
                    </label>
                    <textarea value={formData.comment} onChange={e => handleInputChange('comment', e.target.value)} placeholder="Додаткові побажання..." rows={3} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none" data-id="element-642" />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-643">
                    <input type="checkbox" checked={formData.agreeToTerms} onChange={e => handleInputChange('agreeToTerms', e.target.checked)} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-sky-500 focus:ring-sky-500" data-id="element-644" />
                    <span className="text-sm text-slate-600" data-id="element-645">
                      Я погоджуюсь з{' '}
                      <Link to="/terms" className="text-sky-600 hover:underline" data-id="element-646">
                        умовами використання
                      </Link>{' '}
                      та{' '}
                      <Link to="/privacy" className="text-sky-600 hover:underline" data-id="element-647">
                        політикою конфіденційності
                      </Link>
                    </span>
                  </label>
                  {errors.agreeToTerms && <p className="text-sm text-red-500" data-id="element-648">
                      {errors.agreeToTerms}
                    </p>}
                </div>}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100" data-id="element-649">
                {currentStep > 1 ? <Button variant="ghost" onClick={handlePrevStep} data-id="element-650">
                    Назад
                  </Button> : <Link to="/cart" data-id="element-651">
                    <Button variant="ghost" data-id="element-652">Повернутися до кошика</Button>
                  </Link>}

                {currentStep < 3 ? <Button onClick={handleNextStep} data-id="element-653">Далі</Button> : <Button onClick={handleSubmit} loading={isSubmitting} size="lg" data-id="element-654">
                    Підтвердити замовлення
                  </Button>}
              </div>
            </motion.div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1" data-id="element-655">
            <div className="sticky top-24 bg-slate-50 rounded-2xl p-6" data-id="element-656">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-657">
                Ваше замовлення
              </h3>

              <div className="space-y-3 mb-4" data-id="element-658">
                {cart.map(item => <div key={item.id} className="flex gap-3" data-id="element-659">
                    <img src={item.product.image} alt={item.product.name} className="w-16 h-16 rounded-lg object-cover" data-id="element-660" />
                    <div className="flex-1 min-w-0" data-id="element-661">
                      <p className="text-sm font-medium text-slate-900 line-clamp-2" data-id="element-662">
                        {item.product.name}
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-663">
                        {item.quantity} × {formatPrice(item.product.price)} ₴
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-2" data-id="element-664">
                <div className="flex justify-between text-sm" data-id="element-665">
                  <span className="text-slate-600" data-id="element-666">Товари</span>
                  <span className="font-medium" data-id="element-667">
                    {formatPrice(totalPrice)} ₴
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-668">
                  <span className="text-slate-600" data-id="element-669">Доставка</span>
                  <span className="font-medium" data-id="element-670">
                    {deliveryPrice === 0 ? <span className="text-emerald-600" data-id="element-671">Безкоштовно</span> : `${formatPrice(deliveryPrice)} ₴`}
                  </span>
                </div>
              </div>

              <div className="border-t border-slate-200 mt-4 pt-4" data-id="element-672">
                <div className="flex justify-between" data-id="element-673">
                  <span className="font-semibold text-slate-900" data-id="element-674">Всього</span>
                  <span className="text-xl font-bold text-slate-900" data-id="element-675">
                    {formatPrice(finalTotal)} ₴
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>;
}